
import { useState, useEffect } from "react";
import { FileText, Check, PlusCircle, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { type Feedback, type Changelog } from "@/types";

type ChangelogListProps = {
  companyId: string;
};

export const ChangelogList = ({ companyId }: ChangelogListProps) => {
  const [changelogs, setChangelogs] = useState<Changelog[]>([]);
  const [completedFeedback, setCompletedFeedback] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChangelogs();
    fetchCompletedFeedback();
  }, [companyId]);

  const fetchChangelogs = async () => {
    try {
      const { data, error } = await supabase
        .from("changelogs")
        .select("*")
        .eq("company_id", companyId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setChangelogs(data || []);
    } catch (error) {
      console.error("Error fetching changelogs:", error);
      toast.error("Failed to load changelogs");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletedFeedback = async () => {
    try {
      const { data: latestChangelog } = await supabase
        .from("changelogs")
        .select("created_at")
        .eq("company_id", companyId)
        .order("created_at", { ascending: false })
        .limit(1)
        .single();

      const latestDate = latestChangelog?.created_at || "1970-01-01";

      const { data, error } = await supabase
        .from("feedback")
        .select("*")
        .eq("company_id", companyId)
        .eq("status", "completed")
        .gt("created_at", latestDate)
        .order("created_at", { ascending: false });

      if (error) throw error;
      
      if (data) {
        const formattedFeedback = data.map(item => ({
          ...item,
          votes: 0, // Add missing property
          hasVoted: false, // Add missing property
          score_method: (item.score_method as 'value_effort' | 'rice' | undefined) || 'value_effort'
        }));
        setCompletedFeedback(formattedFeedback);
      } else {
        setCompletedFeedback([]);
      }
    } catch (error) {
      console.error("Error fetching completed feedback:", error);
    }
  };

  const addToChangelog = (feedback: Feedback) => {
    const feedbackText = `## ${feedback.title}\n${feedback.description}`;
    navigator.clipboard.writeText(feedbackText).then(
      () => {
        toast.success("Copied to clipboard! Paste into your changelog description.");
      },
      (err) => {
        console.error("Could not copy text: ", err);
        toast.error("Failed to copy to clipboard");
      }
    );
  };

  const deleteChangelog = async (id: string) => {
    const confirmed = window.confirm("Are you sure you want to delete this changelog?");
    if (!confirmed) return;

    try {
      const { error } = await supabase
        .from("changelogs")
        .delete()
        .eq("id", id);

      if (error) throw error;
      
      toast.success("Changelog deleted successfully");
      setChangelogs(changelogs.filter(item => item.id !== id));
    } catch (error) {
      console.error("Error deleting changelog:", error);
      toast.error("Failed to delete changelog");
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading changelogs...</div>;
  }

  return (
    <div className="space-y-6">
      {completedFeedback.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Recently Completed Feedback</h3>
          <p className="text-sm text-muted-foreground">
            These items have been completed since your last changelog. Click to add to your changelog.
          </p>
          <div className="grid gap-4">
            {completedFeedback.map((item) => (
              <Card key={item.id} className="overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <h4 className="font-medium">{item.title}</h4>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {item.description}
                      </p>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="flex items-center gap-1"
                      onClick={() => addToChangelog(item)}
                    >
                      <PlusCircle className="h-4 w-4" />
                      <span>Copy</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <Separator className="my-6" />
        </div>
      )}

      <h3 className="text-lg font-medium">Recent Changelogs</h3>
      
      {changelogs.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No changelogs yet. Create your first one above.
        </div>
      ) : (
        <div className="space-y-4">
          {changelogs.map((changelog) => (
            <Card key={changelog.id} className="overflow-hidden">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium">{changelog.title}</h3>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8" 
                    onClick={() => deleteChangelog(changelog.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div 
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: changelog.description }}
                />
                <div className="text-xs text-muted-foreground">
                  {new Date(changelog.created_at).toLocaleDateString()}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
